import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { GraduationCap, X, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { useI18n } from "../lib/i18n";

const KEY = "smia_tutorial_done";

const STEPS = [
  {
    fr: ["1. Comprenez l'unité de base", "Un PLAN = une génération vidéo. Aucun moteur ne dépasse 30 s d'un coup : Seedance 2.5 va à 30 s, Seedance Pro et Lite à 12 s, Kling à 10 s, Veo à 8 s. Un film de 30 minutes, ce sont environ 300 plans de 6 s assemblés sur la timeline. C'est la règle qui explique tout le reste."],
    en: ["1. Understand the basic unit", "One SHOT = one video generation. No engine goes beyond 30s at once: Seedance 2.5 reaches 30s, Seedance Pro and Lite 12s, Kling 10s, Veo 8s. A 30-minute film is about 300 six-second shots assembled on the timeline. This rule explains everything else."],
  },
  {
    fr: ["2. Choisissez votre chemin", "Pressé ou débutant → Création rapide : un thème, un sous-template, une phrase, et tout est écrit et lancé pour vous. Vous voulez tout contrôler → le studio complet : AI Director, séquences, plans, timeline."],
    en: ["2. Pick your path", "In a hurry or new → Quick Create: a theme, a sub-template, one sentence, and everything is written and launched for you. Want full control → the full studio: AI Director, sequences, shots, timeline."],
  },
  {
    fr: ["3. Storyboard avant vidéo", "Une image coûte 10 crédits, un plan vidéo standard 70. Validez le cadre en image, puis animez seulement ce qui vous plaît. C'est l'économie la plus rentable du studio : jusqu'à 7× moins cher par essai."],
    en: ["3. Storyboard before video", "An image costs 10 credits, a standard video shot 70. Approve the frame as an image, then animate only what you like. It is the single biggest saving in the studio: up to 7× cheaper per try."],
  },
  {
    fr: ["4. Itérez en Preview et en 480p", "La qualité Preview divise le coût par trois (×0,35) et le 480p par presque deux (×0,6). Cherchez le bon plan au tarif le plus bas, puis refaites uniquement les gardés en 1080p ou en qualité cinéma."],
    en: ["4. Iterate in Preview and 480p", "Preview quality cuts the cost by three (×0.35) and 480p by almost two (×0.6). Find the right shot at the lowest price, then re-render only the keepers at 1080p or cinema quality."],
  },
  {
    fr: ["5. Verrouillez personnages et décors", "Sans Character Lock ni Location Lock, votre personnage change de visage au plan suivant et vous payez deux fois. Créez-les une fois dans la bibliothèque, puis cochez-les sur chaque plan."],
    en: ["5. Lock characters and locations", "Without Character Lock and Location Lock your character changes face in the next shot and you pay twice. Create them once in the library, then tick them on every shot."],
  },
  {
    fr: ["6. Mouvements doux, plans courts", "C'est là que la vidéo IA casse : un léger travelling avant réussit presque toujours, un orbite à 360° presque jamais. Décrivez une seule action par plan et laissez le montage faire le rythme."],
    en: ["6. Gentle moves, short shots", "This is where AI video breaks: a slow push-in nearly always works, a 360° orbit nearly never does. Describe one action per shot and let the edit carry the rhythm."],
  },
  {
    fr: ["7. Comparez les variantes", "Générez 2, 4 ou 8 variantes d'un plan décisif et comparez-les côte à côte dans Variantes. Gardez la meilleure en favori, supprimez le reste. Vous payez chaque variante, donc réservez-les aux plans qui comptent."],
    en: ["7. Compare variations", "Generate 2, 4 or 8 variations of a decisive shot and compare them side by side in Variations. Favourite the best, delete the rest. Every variation is billed, so save them for the shots that matter."],
  },
  {
    fr: ["8. Montez, sonorisez, exportez", "La timeline a 9 pistes : vidéo, b-roll, musique, voix, SFX, texte, sous-titres, VFX, étalonnage. Ajoutez une voix off française native pour environ 10 crédits la minute, puis exportez au format 16:9, 9:16 ou cinéma."],
    en: ["8. Edit, score, export", "The timeline has 9 tracks: video, b-roll, music, voice, SFX, text, subtitles, VFX, grade. Add a native French voice-over for about 10 credits per minute, then export in 16:9, 9:16 or cinema format."],
  },
  {
    fr: ["9. Gardez la main sur vos crédits", "Le coût est toujours affiché avant de générer et remboursé si un job échoue. Vos crédits n'expirent jamais. Activez l'auto-recharge pour qu'un rendu ne s'arrête jamais en plein milieu."],
    en: ["9. Stay in control of your credits", "The cost is always shown before generating and refunded if a job fails. Your credits never expire. Turn on auto-recharge so a render never stops mid-way."],
  },
];

export default function StudioTutorial() {
  const { locale } = useI18n();
  const fr = locale === "fr";
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(KEY)) setOpen(true);
  }, []);

  const close = () => {
    localStorage.setItem(KEY, "1");
    setOpen(false);
  };

  const [title, body] = STEPS[i][fr ? "fr" : "en"];

  return (
    <>
      <button
        onClick={() => { setI(0); setOpen(true); }}
        data-testid="tutorial-open"
        title={fr ? "Guide de démarrage" : "Getting-started guide"}
        className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold"
      >
        <GraduationCap className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{fr ? "Guide" : "Guide"}</span>
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto overscroll-contain bg-black/80 px-4 py-6 backdrop-blur-sm sm:items-center sm:px-5"
          style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))", paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
          data-testid="tutorial-modal"
        >
          <div className="my-auto w-full max-w-[560px] overflow-hidden rounded-2xl border border-[#2a2c3c] bg-[#0b0b11]">
            <div className="flex items-center justify-between border-b border-[#1b1d27] px-5 py-4 sm:px-6">
              <p className="mono text-[10px] uppercase tracking-[0.18em] text-[#8288a0]">
                {fr ? "Guide de démarrage" : "Getting started"} · {i + 1}/{STEPS.length}
              </p>
              <button onClick={close} data-testid="tutorial-close" className="btn-ghost rounded-full p-2">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <div className="max-h-[55vh] overflow-y-auto px-5 py-6 sm:max-h-none sm:px-6 sm:py-7">
              <h3 className="font-display text-lg font-semibold sm:text-xl">{title}</h3>
              <p className="mt-4 text-[13.5px] leading-[1.75] text-[#c0c3d0]">{body}</p>
            </div>

            <div className="flex items-center gap-1 px-5 sm:px-6">
              {STEPS.map((_, k) => (
                <span
                  key={k}
                  className={`h-[3px] flex-1 rounded-full ${k <= i ? "bg-[#ff4b2b]" : "bg-white/10"}`}
                />
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 px-5 py-5 sm:px-6">
              <button
                onClick={close}
                data-testid="tutorial-skip"
                className="mono text-[11px] uppercase tracking-[0.14em] text-[#8288a0]"
              >
                {fr ? "Passer" : "Skip"}
              </button>
              <div className="flex gap-2">
                {i > 0 && (
                  <button onClick={() => setI(i - 1)} data-testid="tutorial-prev" className="btn-ghost inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold">
                    <ChevronLeft className="h-3.5 w-3.5" /> {fr ? "Retour" : "Back"}
                  </button>
                )}
                {i < STEPS.length - 1 ? (
                  <button onClick={() => setI(i + 1)} data-testid="tutorial-next" className="btn-amber inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-[12px] font-semibold">
                    {fr ? "Suivant" : "Next"} <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <Link to="/studio/quick" onClick={close} data-testid="tutorial-finish" className="btn-amber inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-[12px] font-semibold">
                    <Check className="h-3.5 w-3.5" /> {fr ? "Commencer" : "Start now"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
